import { useCallback, useMemo, useRef, useState } from 'react';
import { addError } from '@infra/datadog/tracking';
import { TimeSyncService } from '@services/TimeSyncService';
import { useQueryClient } from '@tanstack/react-query';
import { Chess } from 'chess.js';

import { ChessBoardProps } from '@exports/ChessBoard';
import { MoveInfo, useChessGame } from '@exports/ChessBoard/hooks/useChessGame';
import { useBoardSounds } from '@exports/hooks/useBoardSounds';
import { PromotionPiece, SquareName } from '@libs/chess/types';
import { ApiError } from '@libs/errors';
import { useImmediateEffect } from '@libs/hooks/useImmediateEffect';
import { usePrev } from '@libs/hooks/usePrev';
import { SocketInternalEvents } from '@libs/socket-signalr';

import { COMMON_QUERY_KEYS } from '@features/shared/api';
import {
  components,
  GameSocketEvents,
  useGameMoveV2Mutation,
  useGameQuery,
  useResignMutation,
} from '@features/shared/api/game';
import { useStreamsGameQuery } from '@features/shared/api/streams';

import { IGame } from '../types/game';
import { get2x2TeamPlayerNumber } from '../utils/get2x2TeamPlayerNumber';
import { useDrawOffer } from './useDrawOffer';

/*
    ____,-------------------------------,____
    \   |              Hook             |   /
    /___|-------------------------------|___\
*/

const MOVE_REQUEST_DEADLINE = 5 * 1000;
const MOVE_REQUEST_PROMLEMS_THRESHOLD = 3 * 1000;

type IGameSubscription = {
  subscribe: (cb: (e: GameSocketEvents | SocketInternalEvents) => void) => () => void;
};

export function useGameServer({
  gameId,
  customAccessToken,
  playerId: probablePlayerId,
  isPlaying,
  autoPromotion,
  disablePremove,
  subscription,
  onError,
}: {
  gameId: string;
  customAccessToken?: string;
  playerId?: string | null;
  isPlaying: boolean;
  autoPromotion?: PromotionPiece;
  disablePremove?: boolean;
  subscription: IGameSubscription;
  onError?: (error: Error) => void;
}) {
  // ------------------------------------------
  // Get Game Query

  const queryClient = useQueryClient();

  // для первоначальной загрузки и наблюдателей
  const slowGameQuery = useStreamsGameQuery(gameId, {
    // ВАЖНО: реальные данные получаются из сокета, который и управляет кэшем
    //        useGameQuery
    staleTime: Infinity,
    enabled: !isPlaying,
  });
  // для играющих
  const fastGameQuery = useGameQuery(gameId, {
    // ВАЖНО: реальные данные получаются из сокета, который и управляет кэшем
    //        useGameQuery
    staleTime: Infinity,
    customAccessToken,
    enabled: isPlaying,
  });

  const {
    data: gameData,
    isPending: gamePending,
    error: gameError,
    refetch: refetchGame,
  } = isPlaying ? fastGameQuery : slowGameQuery;

  const gameDataRef = useRef(gameData);
  gameDataRef.current = gameData;

  // ------------------------------------------
  // Metadata

  const lastMoveRecievedTime = useRef<number>(null);

  // ------------------------------------------
  // Credentials

  const playerId = probablePlayerId ?? gameData?.white.id;
  const side = getSide(playerId, gameData);
  const turnSide = gameData?.nextMoveBy === 'Black' ? 'black' : 'white';

  // ------------------------------------------
  // 2x2 Game (two white players and two black players on the same board)

  const is2x2Game = Boolean(gameData?.whiteSecondPlayer && gameData?.blackSecondPlayer);
  const isSecondPlayersTurn =
    is2x2Game && get2x2TeamPlayerNumber(gameData?.moves.length ?? 0) === 2;
  const isSecondPlayer = Boolean(
    is2x2Game &&
    playerId &&
    (playerId === gameData?.whiteSecondPlayer?.id || playerId === gameData?.blackSecondPlayer?.id),
  );
  const is2x2OutOfOrderMove = is2x2Game && isSecondPlayersTurn !== isSecondPlayer;
  const can2x2Premove =
    is2x2Game &&
    side !== 'none' &&
    turnSide !== side &&
    (side === 'white'
      ? isSecondPlayersTurn !== isSecondPlayer
      : isSecondPlayersTurn === isSecondPlayer);

  // ------------------------------------------
  // Move

  const [problemMoveFen, setProblemMoveFen] = useState<string | null>(null);
  const moveProblems = Boolean(problemMoveFen && problemMoveFen === gameData?.currentFen);
  const moveMutation = useGameMoveV2Mutation({ customAccessToken });

  const handleServerMoveRef = useRef(handleServerMove); // HACK
  handleServerMoveRef.current = handleServerMove;

  const handleMove = useCallback(
    (move: MoveInfo) => {
      if (!gameDataRef.current) {
        return;
      }

      const fenBefore = gameDataRef.current.currentFen;

      const deadlineTimeout = setTimeout(() => {
        setProblemMoveFen(fenBefore);
      }, MOVE_REQUEST_PROMLEMS_THRESHOLD);

      const requestStartTime = new Date();
      const timings = calculateMoveTimings(
        gameDataRef.current,
        requestStartTime,
        lastMoveRecievedTime.current,
      );

      const req = {
        id: gameId,
        move: {
          from: move.from,
          to: move.to,
          promoteTo: move.promotion,
          currentFen: fenBefore,
          timings,
        },
        deadlineMs: MOVE_REQUEST_DEADLINE,
      };

      moveMutation.mutate(req, {
        onSuccess(data) {
          if (shouldUpdateGameCache(gameDataRef.current, data)) {
            queryClient.setQueryData(COMMON_QUERY_KEYS.game(gameId), data);
          }
        },
        onError(e) {
          const requestEndDate = new Date();

          // запрос неактуален
          const isStale =
            gameDataRef.current && gameDataRef.current.currentFen !== req.move.currentFen;

          const serverMessage = e instanceof ApiError ? e.serverMessage : null;
          const isGameFinished = !!serverMessage && /^Game .+ is finished$/.test(serverMessage);

          if (!isStale && !isGameFinished) {
            onError?.(e);
          }

          const customError = new Error(serverMessage ?? e.message, {
            cause: e,
          });
          customError.name = isGameFinished
            ? 'GameFinishedMoveError'
            : isStale
              ? 'StaleMoveError'
              : 'CantMoveError';

          addError(customError, {
            req,
            timeSyncDelta: TimeSyncService.instance.delta,
            requestStartTime,
            requestEndDate,
            currentFen: gameDataRef.current?.currentFen,
          });

          // фоном подгрузить новое состояние
          refetchGame().then((d) => {
            // sic! даже если игра не поменялась, форсируем обновление, т.к. change
            // detection в данном случае (откат клиентского  состояния) не сработает
            if (
              d.data &&
              gameDataRef.current &&
              d.data.timestampMs >= gameDataRef.current.timestampMs
            ) {
              handleServerMoveRef.current(d.data);
            }
          });
        },
        onSettled() {
          clearTimeout(deadlineTimeout);
          setProblemMoveFen((f) => (f === fenBefore ? null : f));
        },
      });
    },
    [gameId, moveMutation, onError, queryClient, refetchGame],
  );

  // ------------------------------------------
  // Premove

  const [premove, setPremove] = useState<MoveInfo | null>(null);
  const handlePremove = useCallback((m: MoveInfo | null) => {
    setPremove(m);
  }, []);

  // ------------------------------------------
  // Controller

  const initialSelection = useMemo(() => {
    const lastMove = gameData?.moves[gameData.moves.length - 1];
    return lastMove
      ? {
          action: 'move' as const,
          from: lastMove.from as SquareName,
          to: lastMove.to as SquareName,
        }
      : undefined;
    // ВАЖНО: обрабатываем случай, когда данные закешированы на момент
    // инициализации: доске необходимо явно передать последний ход. В случае без
    // кэша доска определить ход самостоятельно по изменению позиции
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { boardSounds } = useBoardSounds();
  const { props, move, reset } = useChessGame({
    side:
      isPlaying &&
      gameData?.result === 'InProgress' &&
      (!is2x2Game || !is2x2OutOfOrderMove || can2x2Premove)
        ? side
        : 'none',
    initialFen: gameData?.currentFen,
    initialSelection,
    allowPremove: !disablePremove && (!is2x2Game || can2x2Premove),
    autoPromotion,
    boardSounds,
    winner: gameData ? (getWinner(gameData) ?? null) : undefined,
    player: side === 'none' ? undefined : side === 'black' ? 'black' : 'white',
    onMove: handleMove,
    onPremove: handlePremove,
  });

  // ------------------------------------------
  // Draw

  const { canRequestDraw, showDrawOffer, onDrawAccept, onDrawDecline, onDrawOffer } = useDrawOffer({
    game: gameData,
    customAccessToken,
    playerId: is2x2Game && isSecondPlayer ? undefined : playerId,
    onError,
  });

  // ------------------------------------------
  // Resign

  const resignMutation = useResignMutation({ customAccessToken });
  const handleResign = useCallback(() => {
    resignMutation.mutate(gameId, { onError });
  }, [gameId, onError, resignMutation]);

  // ------------------------------------------
  // Handle Server Move

  function handleServerMove(d: IGame) {
    const lastMove = d.moves[d.moves.length - 1];
    if (lastMove) {
      const { from, to, fenBefore, fenAfter, promotedTo } = lastMove;

      try {
        move({
          from: from as SquareName,
          to: to as SquareName,
          fen: fenAfter,
          prevFen: fenBefore,
          promotion: (promotedTo as PromotionPiece) ?? undefined,
        });
      } catch (e) {
        const customError = new Error(
          "Game Data Error: can't apply (server) move. Resetting to latest server FEN",
          {
            cause: e,
          },
        );
        customError.name = 'CantApplyMoveError';

        addError(customError, {
          premove,
          data: d,
          prevData,
        });

        reset(d.currentFen);
      }

      if (premove) {
        // TODO: перенести реализацию полностью на backend
        try {
          // проверяем валидность хода
          const r = new Chess(fenAfter, { enable960: true }).move(premove);

          // применяем локально
          move({
            prevFen: fenAfter,
            fen: r.after,
            from: premove.from,
            to: premove.to,
            promotion: premove.promotion,
          });

          // отсылаем на сервер
          handleMove(premove);
        } catch {
          // do nothing
        } finally {
          setPremove(null);
        }
      }
    } else {
      reset(d.currentFen);
    }
  }

  const prevData = usePrev(gameData);
  if (gameData) {
    if (!prevData) {
      reset(gameData.currentFen);
    } else if (gameData.moves.length > prevData.moves.length) {
      handleServerMove(gameData);
    }
  }

  // ------------------------------------------
  // Socket

  const [socketProblems, setSocketProblems] = useState(false);

  const socketDeps = { playerId, isPlaying };
  const socketDepsRef = useRef(socketDeps);
  socketDepsRef.current = socketDeps;

  useImmediateEffect(() => {
    // первое сообщение в сокет — всегда снэпшот, а не realtime-событие
    let isRealtime = false;

    return subscription.subscribe((e) => {
      switch (e.eventType) {
        case 'GameChanged':
        case 'Surrender':
        case 'DrawRequest':
        case 'DrawAccept':
        case 'DrawDecline': {
          const prevState = gameDataRef.current;

          if (shouldUpdateGameCache(prevState, e.currentState)) {
            queryClient.setQueryData(COMMON_QUERY_KEYS.game(e.gameId), e.currentState);
            gameDataRef.current = e.currentState;

            // Фиксируем тайминги для ходов

            const hasMovesChanged =
              prevState && e.currentState.moves.length > prevState.moves.length;

            const hasGameStarted =
              prevState?.result === 'NotStarted' && e.currentState.result === 'InProgress';

            if (isRealtime && (hasMovesChanged || hasGameStarted)) {
              lastMoveRecievedTime.current = Date.now();
            }
          }

          isRealtime = true;
          break;
        }
        case 'SocketReconnecting':
        case 'SocketClosed': {
          const game = gameDataRef.current;
          const deps = socketDepsRef.current;

          if (e.eventType === 'SocketClosed' && game?.status === 'Finished') {
            break;
          }

          setSocketProblems(true);

          const isActivePlayer = deps.isPlaying && game?.result === 'InProgress';

          if (isActivePlayer) {
            const socketError = new Error(
              `Socket ${e.eventType === 'SocketClosed' ? 'closed' : 'reconnecting'} during active game`,
            );
            socketError.name = `Game${e.eventType}Error`;

            // sic! воспользуемся существующей утилитой
            const { moveTimeStampMs, ...timings } = calculateMoveTimings(
              game,
              new Date(),
              lastMoveRecievedTime.current,
            );

            addError(socketError, {
              gameId,
              playerId: deps.playerId,
              fen: game.currentFen,
              movesCount: game.moves.length,
              errorTimestampMs: moveTimeStampMs,
              ...timings,
            });
          }
          break;
        }
        case 'SocketConnected':
        case 'SocketReconnected':
          isRealtime = false;
          lastMoveRecievedTime.current = null;

          setSocketProblems(false);

          break;
      }
    });
  }, [subscription]);

  // ------------------------------------------
  // Return

  const orientation: 'black' | 'white' = side === 'black' ? 'black' : 'white';
  const position = gameData ? props.position : undefined;
  const interactive =
    isPlaying &&
    gameData?.result === 'InProgress' &&
    (!is2x2Game || !is2x2OutOfOrderMove || can2x2Premove)
      ? props.interactive
      : 'none';

  return {
    error: gameError,
    loading: gamePending,
    networkPromlems: socketProblems || moveProblems,
    //
    propsBoard: {
      ...props,
      orientation,
      interactive,
      position,
    },
    //
    game: gameData,
    side,
    //
    is2x2Game,
    isSecondPlayer,
    isSecondPlayersTurn,
    //
    canRequestDraw: is2x2Game && isSecondPlayer ? false : canRequestDraw,
    showDrawOffer: is2x2Game && isSecondPlayer ? false : showDrawOffer,
    handleOfferDraw: onDrawOffer,
    handleAcceptDraw: onDrawAccept,
    handleDeclineDraw: onDrawDecline,
    //
    handleResign,
  };
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

function shouldUpdateGameCache(prevState: IGame | undefined, nextState: IGame): boolean {
  return (
    !prevState ||
    nextState.timestampMs > prevState.timestampMs ||
    nextState.moves.length > prevState.moves.length ||
    nextState.status !== prevState.status ||
    nextState.result !== prevState.result ||
    (nextState.gameEvents?.length ?? 0) > (prevState.gameEvents?.length ?? 0)
  );
}

function getSide(playerId?: string, data?: IGame): 'none' | 'black' | 'white' {
  if (playerId === data?.white.id || playerId === data?.whiteSecondPlayer?.id) return 'white';
  if (playerId === data?.black.id || playerId === data?.blackSecondPlayer?.id) return 'black';
  return 'none';
}

function getWinner(data?: IGame): ChessBoardProps['winner'] {
  if (data?.result === 'BlackWin') return 'black';
  if (data?.result === 'WhiteWin') return 'white';
  if (data?.result === 'Draw') return 'draw';
  return undefined;
}

function calculateMoveTimings(
  game: IGame,
  requestStartDate: Date,
  lastMoveRecievedTime: number | null,
): components['schemas']['MoveTimings'] {
  const now = requestStartDate.getTime();

  const side = game.nextMoveBy;
  const lastMove = game.moves[game.moves.length - 1];
  const clocks = lastMove?.clockAfter ?? game.clock;
  const timestamp =
    lastMove?.timestampMs ?? (game.status === 'InProgress' ? game.timestampMs : null);

  function calcTime(color: 'White' | 'Black') {
    const timeLeft = color === 'White' ? clocks.whiteLeftMs : clocks.blackLeftMs;

    const syncedTimestamp =
      timestamp && side === color ? TimeSyncService.instance.toClientTimestamp(timestamp) : null;

    const elapsed = syncedTimestamp ? now - syncedTimestamp : 0;

    return timeLeft - elapsed;
  }

  return {
    moveTimeStampMs: now,
    lastMoveReceivedTimeStampMs: lastMoveRecievedTime,
    whiteClockTimeLeftMs: calcTime('White'),
    blackClockTimeLeftMs: calcTime('Black'),
  };
}
